import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  Grid2 as Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  Popover,
} from "@mui/material";
import { FC, memo, useRef, useState } from "react";
import { Question } from "types/question";
import { Option } from "types/option";
import { Add, Delete, Edit, ArrowUpward, ArrowDownward, InsertEmoticon } from "@mui/icons-material";
import { ModalWithTextArea } from "components/Modal/ModalWithTextArea";
import { CardOutline } from "components/Card";

const QUESTION_TYPES = [
  { label: "Fill in the blank", value: "open_ended" },
  { label: "Multiple Choice (select multiple)", value: "multiple_choice" },
  { label: "Multiple Choice (select one)", value: "single_choice" },
  { label: "Likert Scale", value: "likert" },
];

type TableQuestionsComponentProps = {
  questions: Question[];
  setQuestions: (questions: Question[]) => void;
};

const TableQuestionsComponent: FC<TableQuestionsComponentProps> = ({
  questions,
  setQuestions,
}) => {
  const [openQuestionEdit, setOpenQuestionEdit] = useState<boolean>(false);
  const questionIndexSelected = useRef<number>(0);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [emojiTarget, setEmojiTarget] = useState<{ questionIndex: number; optionIndex: number } | null>(null);

  // Common emojis for Likert scales
  const commonEmojis = [
    "😢", "😞", "😐", "😊", "😄",
    "😭", "😔", "😑", "🙂", "😁",
    "😡", "😕", "😶", "😌", "😃",
    "😰", "😟", "😐", "😎", "🤩",
  ];

  const onChangeQuestionValue = (
    index: number,
    key: string,
    value: string | number | Option[],
  ) => {
    const newQuestions = questions.map((q, i) => {
      if (i === index) {
        const updatedQuestion = { ...q, [key]: value };
        
        // If changing to Likert type, initialize default options if none exist
        if (key === "type" && value === "likert" && (!q.options || q.options.length === 0)) {
          updatedQuestion.options = [
            { text: "😢 Very Dissatisfied", order: 1, question_id: "" },
            { text: "😞 Dissatisfied", order: 2, question_id: "" },
            { text: "😐 Neutral", order: 3, question_id: "" },
            { text: "😊 Satisfied", order: 4, question_id: "" },
            { text: "😄 Very Satisfied", order: 5, question_id: "" },
          ];
        }
        
        return updatedQuestion;
      }
      return q;
    });
    setQuestions(newQuestions);
  };

  const onChangeOptionValue = (
    questionIndex: number,
    optionIndex: number,
    key: string,
    value: string | number,
  ) => {
    const newQuestions = questions.map((q, i) => {
      if (i === questionIndex) {
        const newOptions = (q.options || []).map((opt, oi) => {
          if (oi === optionIndex) {
            return { ...opt, [key]: value };
          }
          return opt;
        });
        return { ...q, options: newOptions };
      }
      return q;
    });
    setQuestions(newQuestions);
  };

  const handleEmojiClick = (event: React.MouseEvent<HTMLButtonElement>, questionIndex: number, optionIndex: number) => {
    setEmojiAnchorEl(event.currentTarget);
    setEmojiTarget({ questionIndex, optionIndex });
  };

  const handleEmojiSelect = (emoji: string) => {
    if (emojiTarget) {
      const question = questions[emojiTarget.questionIndex];
      const option = question.options?.[emojiTarget.optionIndex];
      if (option) {
        const currentText = option.text || "";
        // Insert emoji at the start if text doesn't start with emoji, otherwise replace first emoji
        const newText = currentText.match(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u)
          ? emoji + currentText.replace(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]\s*/u, "")
          : emoji + (currentText ? " " + currentText : "");
        onChangeOptionValue(emojiTarget.questionIndex, emojiTarget.optionIndex, "text", newText);
      }
    }
    setEmojiAnchorEl(null);
    setEmojiTarget(null);
  };

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null);
    setEmojiTarget(null);
  };

  const addNewQuestion = () => {
    const maxOrder = questions.length > 0
      ? Math.max(...questions.map((q) => q.order || 0))
      : 0;
    setQuestions([
      ...questions,
      {
        title: "",
        type: "open_ended",
        body: "",
        order: maxOrder + 1,
        options: [] as Option[],
      },
    ]);
  };

  const addNewOption = (questionIndex: number) => {
    const question = questions[questionIndex];
    const maxOrder = (question.options || []).length > 0
      ? Math.max(...(question.options || []).map((opt) => opt.order || 0))
      : 0;
    const newOptions = [
      ...(question.options || []),
      {
        text: "",
        order: maxOrder + 1,
        question_id: "",
      },
    ];
    onChangeQuestionValue(questionIndex, "options", newOptions);
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const question = questions[questionIndex];
    const newOptions = (question.options || []).filter((_, i) => i !== optionIndex);
    onChangeQuestionValue(questionIndex, "options", newOptions);
  };

  const moveQuestion = (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === questions.length - 1)
    ) {
      return;
    }
    const newQuestions = [...questions];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newQuestions[index], newQuestions[targetIndex]] = [
      newQuestions[targetIndex],
      newQuestions[index],
    ];
    // Update orders
    newQuestions.forEach((q, i) => {
      q.order = i + 1;
    });
    setQuestions(newQuestions);
  };

  const moveOption = (
    questionIndex: number,
    optionIndex: number,
    direction: "up" | "down",
  ) => {
    const question = questions[questionIndex];
    const options = question.options || [];
    if (
      (direction === "up" && optionIndex === 0) ||
      (direction === "down" && optionIndex === options.length - 1)
    ) {
      return;
    }
    const newOptions = [...options];
    const targetIndex = direction === "up" ? optionIndex - 1 : optionIndex + 1;
    [newOptions[optionIndex], newOptions[targetIndex]] = [
      newOptions[targetIndex],
      newOptions[optionIndex],
    ];
    // Update orders
    newOptions.forEach((opt, i) => {
      opt.order = i + 1;
    });
    onChangeQuestionValue(questionIndex, "options", newOptions);
  };

  return (
    <Box>
      {questions.length === 0 && (
        <Box pt={1}>
          <Alert severity="info">No questions, add more.</Alert>
        </Box>
      )}
      {questions.length > 0 && (
        <>
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            {questions.map((question, qIndex) => (
              <CardOutline key={`question_${qIndex}`} sx={{ marginBottom: 2 }}>
                <Box sx={{ padding: 1 }}>
                  <Stack direction="column" spacing={2}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Question Title"
                          value={question.title}
                          onChange={(e) =>
                            onChangeQuestionValue(qIndex, "title", e.target.value)
                          }
                          size="small"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={question.type || "open_ended"}
                            label="Type"
                            onChange={(e) =>
                              onChangeQuestionValue(qIndex, "type", e.target.value)
                            }
                          >
                            {QUESTION_TYPES.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => moveQuestion(qIndex, "up")}
                            disabled={qIndex === 0}
                          >
                            <ArrowUpward />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveQuestion(qIndex, "down")}
                            disabled={qIndex === questions.length - 1}
                          >
                            <ArrowDownward />
                          </IconButton>
                        </Stack>
                      </Grid>
                    </Grid>
                    <Box>
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        {question.body && question.body !== "" ? (
                          <Typography
                            variant="body2"
                            sx={{ paddingRight: "50px", px: 1 }}
                          >
                            <strong>Body:</strong>{" "}
                            {question.body.length > 64
                              ? question.body.slice(0, 64) + "..."
                              : question.body}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            align="center"
                            color="text.secondary"
                            sx={{ fontStyle: "italic" }}
                          >
                            Click the pencil to add the body of the question
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {(question.type === "multiple_choice" || question.type === "single_choice" || question.type === "likert") && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Options:
                        </Typography>
                        {(question.options || []).map((option, oIndex) => (
                          <Box key={`option_${oIndex}`} mb={1}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField
                                fullWidth
                                size="small"
                                label={`Option ${oIndex + 1}`}
                                value={option.text}
                                onChange={(e) =>
                                  onChangeOptionValue(
                                    qIndex,
                                    oIndex,
                                    "text",
                                    e.target.value,
                                  )
                                }
                                sx={{ flex: 1 }}
                              />
                              <IconButton
                                size="small"
                                onClick={(e) => handleEmojiClick(e, qIndex, oIndex)}
                                sx={{ color: "primary.main" }}
                              >
                                <InsertEmoticon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => moveOption(qIndex, oIndex, "up")}
                                disabled={oIndex === 0}
                              >
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => moveOption(qIndex, oIndex, "down")}
                                disabled={
                                  oIndex === (question.options || []).length - 1
                                }
                              >
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeOption(qIndex, oIndex)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        ))}
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Add />}
                          onClick={() => addNewOption(qIndex)}
                          fullWidth
                        >
                          Add Option
                        </Button>
                      </Box>
                    )}
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Button
                          variant="outlined"
                          color="error"
                          fullWidth
                          onClick={() => removeQuestion(qIndex)}
                          size="small"
                          startIcon={<Delete />}
                        >
                          Remove
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          size="small"
                          onClick={() => {
                            setOpenQuestionEdit(true);
                            questionIndexSelected.current = qIndex;
                            setSelectedQuestion(question);
                          }}
                          startIcon={<Edit />}
                        >
                          Edit Body
                        </Button>
                      </Grid>
                    </Grid>
                  </Stack>
                </Box>
              </CardOutline>
            ))}
          </Box>
          <TableContainer sx={{ display: { xs: "none", md: "table" } }}>
            <Table sx={{ minWidth: 650 }} aria-label="table questions">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Body</TableCell>
                  <TableCell>Options</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {questions.map((question, qIndex) => (
                  <TableRow
                    key={`question_${qIndex}`}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => moveQuestion(qIndex, "up")}
                          disabled={qIndex === 0}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveQuestion(qIndex, "down")}
                          disabled={qIndex === questions.length - 1}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={question.type || "open_ended"}
                          onChange={(e) =>
                            onChangeQuestionValue(qIndex, "type", e.target.value)
                          }
                        >
                          {QUESTION_TYPES.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <TextField
                        size="small"
                        value={question.title}
                        onChange={(e) =>
                          onChangeQuestionValue(qIndex, "title", e.target.value)
                        }
                        placeholder="Question title"
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0 }}>
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        {question.body && question.body !== "" ? (
                          <Typography
                            variant="body2"
                            sx={{ paddingRight: "50px" }}
                          >
                            {question.body.length > 64
                              ? question.body.slice(0, 64) + "..."
                              : question.body}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ 
                              paddingRight: "50px",
                              fontStyle: "italic",
                            }}
                          >
                            Click the pencil to add the body of the question
                          </Typography>
                        )}
                        <IconButton
                          sx={{
                            position: "absolute",
                            right: 0,
                            top: !question.body || question.body === "" ? "-15px" : "-5px",
                          }}
                          color="primary"
                          size="small"
                          onClick={() => {
                            setOpenQuestionEdit(true);
                            questionIndexSelected.current = qIndex;
                            setSelectedQuestion(question);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {(question.type === "multiple_choice" || question.type === "single_choice" || question.type === "likert") ? (
                        <Box>
                          {(question.options || []).map((option, oIndex) => (
                            <Stack
                              key={`option_${oIndex}`}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              mb={0.5}
                            >
                              <TextField
                                size="small"
                                value={option.text}
                                onChange={(e) =>
                                  onChangeOptionValue(
                                    qIndex,
                                    oIndex,
                                    "text",
                                    e.target.value,
                                  )
                                }
                                placeholder={`Option ${oIndex + 1}`}
                                sx={{ flex: 1 }}
                              />
                              <IconButton
                                size="small"
                                onClick={(e) => handleEmojiClick(e, qIndex, oIndex)}
                                sx={{ color: "primary.main" }}
                              >
                                <InsertEmoticon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => moveOption(qIndex, oIndex, "up")}
                                disabled={oIndex === 0}
                              >
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => moveOption(qIndex, oIndex, "down")}
                                disabled={
                                  oIndex === (question.options || []).length - 1
                                }
                              >
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeOption(qIndex, oIndex)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          ))}
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => addNewOption(qIndex)}
                          >
                            Add Option
                          </Button>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => removeQuestion(qIndex)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Box mt={2}>
        <Button
          onClick={addNewQuestion}
          variant="outlined"
          fullWidth
          startIcon={<Add />}
        >
          Add new question
        </Button>
      </Box>
      {openQuestionEdit && (
        <ModalWithTextArea
          title="Question Body"
          open={openQuestionEdit}
          defaultValue={selectedQuestion?.body ?? ""}
          onClose={() => setOpenQuestionEdit(false)}
          onComplete={(text) => {
            onChangeQuestionValue(
              questionIndexSelected.current,
              "body",
              text,
            );
            setOpenQuestionEdit(false);
          }}
        />
      )}
      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={handleEmojiClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
            Select emoji:
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 1,
            }}
          >
            {commonEmojis.map((emoji, index) => (
              <IconButton
                key={index}
                onClick={() => handleEmojiSelect(emoji)}
                sx={{
                  fontSize: "1.5rem",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    transform: "scale(1.2)",
                  },
                  transition: "transform 0.2s",
                }}
              >
                {emoji}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export const TableQuestions = memo(TableQuestionsComponent);
